/**
 * factorDisplay.ts
 *
 * Shared helpers for rendering factor rows in the sphere-status drill-downs
 * (StatusPopoverMolecule, MobileStatusDrawerMolecule, StatusSummaryMode).
 * Extracting these here keeps SonarQube's duplicated-code smell in check —
 * the previous inline copies of `isOnCard` + `getFactorName` diverged
 * subtly between the components (StatusSummaryMode couldn't cross-reference
 * factorDefinitions because its data source doesn't expose them).
 */

/** A minimal factor shape the show-on-cards filter needs. */
export interface ShowOnCardsSource {
  id?: string;
  showOnCards?: boolean;
}

/** A minimal factor-definition shape (from the sphere GET). */
export interface FactorDefinitionRef {
  id: string;
  showOnCards?: boolean;
}

/**
 * Build a lookup that maps a factor id to its LIVE `showOnCards` value.
 *
 * The status point's `currentValue.factors[]` summary is written at
 * recompute time and can lag the vertex — so consumers that have access to
 * the live `factorDefinitions` array should cross-reference through this
 * lookup rather than trusting the summary alone. When a component only has
 * the summary (e.g. StatusSummaryMode), pass an empty array; the fallback
 * to the summary's own `showOnCards` still works.
 */
export function buildShowOnCardsLookup(
  factorDefinitions: readonly FactorDefinitionRef[] | undefined,
): Map<string, { showOnCards?: boolean }> {
  return new Map(
    (factorDefinitions ?? []).map((f) => [f.id, { showOnCards: f.showOnCards }]),
  );
}

/**
 * EAINT-750: whether a factor should surface in the per-factor drill-down.
 *
 * Only an explicit `false` opts out. Absence, `null`, `undefined`, and
 * `true` all resolve to shown so legacy factors keep behaving as before.
 *
 * If `definitionsById` is provided AND has a live entry for this factor,
 * the live value wins; otherwise fall back to whatever the summary object
 * carries. This mirrors backend `resolveShowOnCards`.
 */
export function isFactorOnCard(
  factor: ShowOnCardsSource,
  definitionsById?: Map<string, { showOnCards?: boolean }>,
): boolean {
  const live = factor.id ? definitionsById?.get(factor.id)?.showOnCards : undefined;
  const effective = live ?? factor.showOnCards;
  return effective !== false;
}

/**
 * The subset of a factor object that the display-name resolver reads.
 * Both `name` (surfaced on the summary by `calculateStatusPoint`) and
 * `metadata.name` (on the vertex-shaped `factorDefinition`) count.
 */
export interface FactorNameSource {
  name?: string;
  metadata?: { name?: string };
}

/**
 * Resolve the owner-visible name for a factor.
 *
 * Prefer the owner-set label from either shape (`name` on summary,
 * `metadata.name` on definition). Fall back to the caller's localized
 * type label only when the owner never set a name — otherwise a sphere
 * with five manual factors renders as "Manual · Manual · Manual · Manual
 * · Manual".
 *
 * The type-label fallback is passed in as a string rather than derived
 * here so the caller can localize / capitalize / stylize it however the
 * surrounding component already does.
 */
export function resolveFactorDisplayName(
  factor: FactorNameSource,
  typeLabelFallback: string,
): string {
  const owner = factor.name?.trim() || factor.metadata?.name?.trim();
  return owner || typeLabelFallback;
}
